import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  type BasePuiDialogProps,
  ButtonWithLoader,
  type CustomFieldValidatorState,
  type Patient,
  PuiDialog,
  useFields,
  Validators,
} from '@pbt/pbt-ui-components'

import type { Clinic } from '~/api/graphql/generated/types'
import type { TimetableEvent } from '~/types'

import PMRConsentStep from '../PMRConsentStep'
import {
  PetParentConsentValue,
  PMRConsentValue,
} from '../types/appointmentEnums'
import type { PreviousMedicalRecordConsent } from '../types/clinicTypes'

import styles from './EditPMRDialog.module.scss'

const getInitialHasSeenVeterinarian = (
  pmrConsent: PreviousMedicalRecordConsent | undefined,
): PMRConsentValue => {
  if (!pmrConsent) return PMRConsentValue.EMPTY
  if (pmrConsent.previousClinicContacts?.length > 0) return PMRConsentValue.YES
  if (pmrConsent.consent === false) return PMRConsentValue.NO
  return PMRConsentValue.UNKNOWN
}

const getInitialPetParentConsent = (
  pmrConsent: PreviousMedicalRecordConsent | undefined,
): PetParentConsentValue => {
  if (!pmrConsent || pmrConsent.consent === null)
    return PetParentConsentValue.EMPTY
  return pmrConsent.consent
    ? PetParentConsentValue.YES
    : PetParentConsentValue.NO
}

const getInitialPreviousClinics = (
  pmrConsent: PreviousMedicalRecordConsent | undefined,
): Clinic[] => pmrConsent?.previousClinicContacts ?? []

export interface EditPMRDialogProps extends BasePuiDialogProps {
  appointment: TimetableEvent
  patient?: Patient | null
}

const EditPMRDialog = ({
  open,
  onClose,
  appointment,
  patient,
}: EditPMRDialogProps) => {
  const { t } = useTranslation(['Common', 'TimeTable', 'Dialogs'])

  const pmrConsent: PreviousMedicalRecordConsent | undefined =
    // @ts-ignore previousMedicalRecordConsent doesn't exist yet, will be added to public api
    appointment?.previousMedicalRecordConsent

  const validatePreviousClinics = ({
    state,
    value,
  }: CustomFieldValidatorState) => {
    const hasSeenVet = state.hasSeenVeterinarian
    const showFindPreviousPractice = hasSeenVet === PMRConsentValue.YES
    const clinics = (value ?? []) as Clinic[]
    return !showFindPreviousPractice || clinics.length > 0
  }

  const validatePetParentConsent = ({
    state,
    value,
  }: CustomFieldValidatorState) => {
    const hasSeenVet = state.hasSeenVeterinarian
    const clinics = (state.previousClinics ?? []) as Clinic[]
    const showConsentAndNotesSections =
      clinics.length > 0 && hasSeenVet === PMRConsentValue.YES
    return !showConsentAndNotesSections || Validators.notEmptyFormField(value)
  }

  const { fields: pmrFields, validate: validatePmrFields } = useFields(
    [
      {
        name: 'hasSeenVeterinarian',
        label: t('TimeTable:PMR_CONSENT.HAS_SEEN_VETERINARIAN_LABEL'),
        initialValue: getInitialHasSeenVeterinarian(pmrConsent),
        validators: ['required'],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_SELECT_OPTION_ERROR'),
        },
      },
      {
        name: 'previousClinics',
        label: t('TimeTable:PMR_CONSENT.PREVIOUS_PRACTICE_NAME_LABEL'),
        initialValue: getInitialPreviousClinics(pmrConsent),
        validators: [
          {
            validator: validatePreviousClinics,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_SELECT_PRACTICE_ERROR'),
        },
      },
      {
        name: 'petParentConsent',
        label: t('TimeTable:PMR_CONSENT.PET_PARENT_CONSENT_LABEL'),
        initialValue: getInitialPetParentConsent(pmrConsent),
        validators: [
          {
            validator: validatePetParentConsent,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_SELECT_OPTION_ERROR'),
        },
      },
      {
        name: 'internalNote',
        initialValue: appointment?.internalNotes || '',
      },
    ],
    false,
  )

  const handleUpdate = () => {
    validatePmrFields()
  }

  const handleCancel = () => {
    onClose?.()
  }

  return (
    <PuiDialog
      aria-labelledby="edit-pmr-dialog"
      classes={{ paper: styles.paper }}
      open={open}
      title={t('TimeTable:PMR_CONSENT.EDIT_PREVIOUS_MEDICAL_RECORDS_TITLE')}
      onClose={onClose}
    >
      <div className={styles.content}>
        <PMRConsentStep
          insideEditPMRDialog
          fields={{
            hasSeenVeterinarian: pmrFields.hasSeenVeterinarian,
            internalNote: pmrFields.internalNote,
            petParentConsent: pmrFields.petParentConsent,
            previousClinics: pmrFields.previousClinics,
          }}
          headerPadding={3}
          patient={patient}
        />
      </div>
      <div className={styles.footer}>
        <ButtonWithLoader onClick={handleUpdate}>
          {t('Common:UPDATE_ACTION')}
        </ButtonWithLoader>
        <ButtonWithLoader
          className={styles.cancelButton}
          color="secondary"
          onClick={handleCancel}
        >
          {t('Common:CANCEL_ACTION')}
        </ButtonWithLoader>
      </div>
    </PuiDialog>
  )
}

export default EditPMRDialog
